import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2,
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Ban,
  ShieldCheck,
  Undo2,
  RotateCcw,
} from 'lucide-react';
import { DnaTableV4 } from '@/design-system/DnaDesignSystem';

const API = process.env.REACT_APP_BACKEND_URL;

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────
const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
};

// V4.48 · Parser robusto de erros HTTP — evita "[object Object]" no toast
const getErrorMessage = (err, fallback = 'Falha inesperada') => {
  const detail = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors → array de {loc, msg, type}
    return detail.map((d) => {
      if (typeof d === 'string') return d;
      const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : null;
      const msg = d?.msg || JSON.stringify(d);
      return field ? `Campo obrigatório ausente: ${field} (${msg})` : msg;
    }).join(' · ');
  }
  if (typeof detail === 'object') {
    // Ex: {message, invalid_reasons: [{signature, reason}, ...]}
    const parts = [];
    if (detail.message) parts.push(detail.message);
    if (Array.isArray(detail.invalid_reasons) && detail.invalid_reasons.length) {
      const sample = detail.invalid_reasons.slice(0, 3)
        .map((r) => r?.reason || r?.msg || JSON.stringify(r))
        .join(' · ');
      parts.push(`(${detail.invalid_reasons.length} inválidos: ${sample}${detail.invalid_reasons.length > 3 ? '…' : ''})`);
    }
    if (parts.length) return parts.join(' ');
    if (detail.msg) return detail.msg;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail);
};

const STATUS_BADGE = {
  match_ok: { label: 'Conciliado', variant: 'default', color: 'dna-tone-success' },
  ambiguous: { label: 'Ambíguo', variant: 'secondary', color: 'dna-tone-warning' },
  not_found: { label: 'Não localizado', variant: 'destructive', color: 'dna-tone-danger' },
  already_processed: { label: 'Já processado', variant: 'outline', color: 'dna-tone-neutral' },
};

// ────────────────────────────────────────────
// Preview Modal
// ────────────────────────────────────────────
function PreviewModal({ preview, onClose, onConfirm, confirming }) {
  const [approvedSigs, setApprovedSigs] = useState(() => {
    const s = new Set();
    (preview?.records || []).forEach((r) => {
      if (r.status_match === 'match_ok') s.add(r.record_signature);
    });
    return s;
  });
  const toggle = (sig, enabled) => {
    setApprovedSigs((prev) => {
      const n = new Set(prev);
      if (enabled) n.add(sig);
      else n.delete(sig);
      return n;
    });
  };
  // V4.46 · Selecionar todos os boletos elegíveis (match_ok) de uma vez
  const approvableRecords = (preview?.records || []).filter(r => r.status_match === 'match_ok');
  const allSelected = approvableRecords.length > 0 && approvableRecords.every(r => approvedSigs.has(r.record_signature));
  const someSelected = approvableRecords.some(r => approvedSigs.has(r.record_signature));
  const toggleAll = () => {
    setApprovedSigs(() => {
      if (allSelected) return new Set();
      return new Set(approvableRecords.map(r => r.record_signature));
    });
  };
  if (!preview) return null;
  const t = preview.totals || {};
  const alreadyProcessed = preview.already_processed;
  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="retorno-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-purple-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-purple-700" />
            Prévia do arquivo de retorno · Banco Safra 422 · CNAB 400
          </DialogTitle>
        </DialogHeader>

        {alreadyProcessed && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="retorno-already-processed-warn">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-900">
              <b>Arquivo já enviado anteriormente.</b> Você pode confirmar somente os registros marcados como <b>Conciliado</b> — os que já refletem em <code>db.titles</code> aparecem como <b>Já processado</b> e não podem ser rebaixados (idempotência SHA-256 + assinatura de pagamento).
            </div>
          </div>
        )}

        {/* Cabeçalho do arquivo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="retorno-header-info">
          <div className="rounded border border-slate-200 p-2">
            <div className="text-slate-500">Banco</div>
            <div className="font-semibold text-slate-800">{preview.bank_name} — {preview.bank_code}</div>
          </div>
          <div className="rounded border border-slate-200 p-2">
            <div className="text-slate-500">Layout</div>
            <div className="font-semibold text-slate-800">{preview.layout} · Cobrança</div>
          </div>
          <div className="rounded border border-slate-200 p-2">
            <div className="text-slate-500">Empresa</div>
            <div className="font-semibold text-slate-800">{preview.company_name || '—'}</div>
          </div>
          <div className="rounded border border-slate-200 p-2">
            <div className="text-slate-500">Data do arquivo</div>
            <div className="font-semibold text-slate-800">{fmtDate(preview.file_date)}</div>
          </div>
        </div>

        {/* Totais */}
        <div className="grid grid-cols-4 gap-3 text-center" data-testid="retorno-totals-grid">
          <div className="rounded bg-slate-50 border border-slate-200 p-2">
            <div className="text-[10px] uppercase text-slate-500">Registros</div>
            <div className="text-lg font-semibold text-slate-800">{t.records ?? 0}</div>
          </div>
          <div className="rounded bg-emerald-50 border border-emerald-200 p-2">
            <div className="text-[10px] uppercase text-emerald-700">Valor nominal</div>
            <div className="text-lg font-semibold text-emerald-800">{fmtBRL(t.original_amount)}</div>
          </div>
          <div className="rounded bg-purple-50 border border-purple-200 p-2">
            <div className="text-[10px] uppercase text-purple-700">Valor recebido</div>
            <div className="text-lg font-semibold text-purple-900">{fmtBRL(t.paid_amount)}</div>
          </div>
          <div className="rounded bg-amber-50 border border-amber-200 p-2">
            <div className="text-[10px] uppercase text-amber-700">Acréscimos</div>
            <div className="text-lg font-semibold text-amber-800">{fmtBRL(t.additions_amount)}</div>
          </div>
        </div>

        {/* V4.46 · Barra de seleção rápida */}
        {!alreadyProcessed && approvableRecords.length > 0 && (
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs" data-testid="retorno-select-bar">
            <div className="text-slate-600">
              <b className="text-slate-900">{approvedSigs.size}</b> de <b>{approvableRecords.length}</b> boletos válidos selecionados
              {approvableRecords.length < (preview.records?.length || 0) && (
                <span className="text-slate-400 ml-1">({(preview.records?.length || 0) - approvableRecords.length} não elegíveis)</span>
              )}
            </div>
            <button
              type="button"
              onClick={toggleAll}
              disabled={confirming}
              className="px-3 py-1 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="retorno-select-all-btn"
            >
              {allSelected ? 'Desmarcar todos' : `Marcar todos os ${approvableRecords.length}`}
            </button>
          </div>
        )}

        {/* Tabela de registros */}
        <div className="rounded-md border border-slate-200 overflow-auto" data-testid="retorno-records-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    onChange={toggleAll}
                    disabled={confirming || approvableRecords.length === 0}
                    title={allSelected ? 'Desmarcar todos' : `Marcar todos os ${approvableRecords.length} boletos Conciliados`}
                    data-testid="retorno-select-all"
                  />
                </th>
                <th className="text-left px-3 py-2">Nosso nº / Controle</th>
                <th className="text-left px-3 py-2">Pagador</th>
                <th className="text-left px-3 py-2">Documento</th>
                <th className="text-center px-3 py-2">Ocorrência</th>
                <th className="text-center px-3 py-2">Vencimento</th>
                <th className="text-center px-3 py-2">Crédito</th>
                <th className="text-right px-3 py-2">Original</th>
                <th className="text-right px-3 py-2">Recebido</th>
                <th className="text-right px-3 py-2">Acréscimos</th>
                <th className="text-center px-3 py-2">Conciliação</th>
              </tr>
            </thead>
            <tbody>
              {(preview.records || []).map((r) => {
                const s = STATUS_BADGE[r.status_match] || STATUS_BADGE.not_found;
                const canApprove = r.status_match === 'match_ok';
                return (
                  <tr key={r.record_signature} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={approvedSigs.has(r.record_signature)}
                        onChange={(e) => toggle(r.record_signature, e.target.checked)}
                        disabled={!canApprove || confirming}
                        data-testid={`retorno-approve-${r.record_signature.slice(0, 8)}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono">
                      <div className="font-semibold text-slate-800">{r.our_number}</div>
                      {r.control_number !== r.our_number && (
                        <div className="text-[10px] text-slate-500">ctrl: {r.control_number}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-800">
                      <div>{r.payer_name || '—'}</div>
                      {r.candidates?.[0]?.student_name && (
                        <div className="text-[10px] text-purple-700">Aluno: {r.candidates[0].student_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.document_number || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="font-semibold text-slate-800">{r.occurrence_code}</div>
                      <div className="text-[10px] text-slate-500">{r.occurrence_desc}</div>
                    </td>
                    <td className="px-3 py-2 text-center text-slate-700">{fmtDate(r.due_date)}</td>
                    <td className="px-3 py-2 text-center text-slate-700">{fmtDate(r.credit_date)}</td>
                    <td className="px-3 py-2 text-right text-emerald-800">{fmtBRL(r.original_amount)}</td>
                    <td className="px-3 py-2 text-right text-purple-900 font-semibold">{fmtBRL(r.paid_amount)}</td>
                    <td className="px-3 py-2 text-right text-amber-800">{fmtBRL(r.additions_amount)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-semibold ${s.color}`}>
                        {s.label}
                      </span>
                      {r.candidates?.length > 0 && (
                        <div className="text-[10px] text-slate-500 mt-1">{r.candidates.length} candidato(s)</div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(!preview.records || preview.records.length === 0) && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">Nenhum registro no arquivo.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="retorno-selected-summary">
          <ShieldCheck className="w-3.5 h-3.5 text-purple-700" />
          <b>{approvedSigs.size}</b> registro(s) selecionado(s) para baixa. Nenhuma alteração
          é aplicada até você confirmar. Valores em <b>centavos</b>. Principal e acréscimos
          são armazenados <b>separadamente</b>.
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={onClose} disabled={confirming} data-testid="retorno-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={() => onConfirm(Array.from(approvedSigs))}
            disabled={confirming || approvedSigs.size === 0}
            className="bg-gradient-to-r from-emerald-600 to-purple-700 text-white"
            data-testid="retorno-confirm-btn"
            title={approvedSigs.size === 0 ? 'Marque ao menos 1 registro Conciliado' : undefined}
          >
            {confirming ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Aplicando baixa…</> : <><CheckCircle2 className="w-4 h-4 mr-1" />Confirmar baixa de {approvedSigs.size} registro(s)</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────
// Página principal
// ────────────────────────────────────────────
export default function DnaPayRetornos() {
  const [arquivos, setArquivos] = useState([]);
  const [resumo, setResumo] = useState(null);   // cards mensais (principal/multa/juros/total)
  const [mesSel, setMesSel] = useState('');     // mês escolhido nos cards ('' = mais recente)
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const fetchData = async () => {
    try {
      // nova pipeline (v4.27)
      const r = await api.get('/dnapay/safra/retorno/arquivos', { params: { limit: 100 } });
      setArquivos(r.data?.arquivos || []);
    } catch (e) {
      // fallback silencioso
      setArquivos([]);
    }
    try {
      const m = await api.get('/dnapay/safra/retorno/resumo-mensal', { params: { months: 12 } });
      setResumo(m.data || null);
    } catch (e) { setResumo(null); }
  };

  useEffect(() => { fetchData(); fetchDivergences(); }, []);

  // V4.86 · Drill-down por arquivo + download RET + estorno
  const [auditOpen, setAuditOpen] = useState(null);
  const [auditData, setAuditData] = useState(null);
  // V4.92 · Reverter retorno
  const [revertTarget, setRevertTarget] = useState(null); // arquivo alvo
  const [revertMode, setRevertMode] = useState('processing_only');
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);
  const doRevert = async () => {
    if (!revertTarget) return;
    const reason = (revertReason || '').trim();
    if (reason.length < 8) { toast.error('Motivo obrigatório (mín. 8 caracteres).'); return; }
    if (revertMode === 'processing_and_payments') {
      const ok = window.confirm(
        `Você vai DESFAZER as baixas em db.titles deste arquivo (${revertTarget.filename}).\n` +
        `Isso reverte apenas pagamentos com origem CNAB deste arquivo.\n\nTem certeza?`
      );
      if (!ok) return;
    }
    setReverting(true);
    try {
      const { data } = await api.post(
        `/dnapay/safra/retorno/arquivos/${revertTarget.id}/revert`,
        { reason, mode: revertMode }
      );
      toast.success(
        `Arquivo revertido. ` +
        (revertMode === 'processing_and_payments'
          ? `${data.titles_reverted} baixa(s) desfeita(s), ${data.payments_reverted} pagamento(s) marcado(s).`
          : `Você já pode reenviar/reprocessar o arquivo.`)
      );
      setRevertTarget(null); setRevertReason(''); setRevertMode('processing_only');
      await fetchData();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao reverter.');
    } finally { setReverting(false); }
  };
  const openAudit = async (fileId) => {
    if (auditOpen === fileId) { setAuditOpen(null); setAuditData(null); return; }
    try {
      const { data } = await api.get(`/dnapay/safra/retorno/audit/${fileId}`);
      setAuditData(data);
      setAuditOpen(fileId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao abrir auditoria.');
    }
  };
  const downloadRet = async (fileId, filename) => {
    try {
      const resp = await api.get(`/dnapay/safra/retorno/download/${fileId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename || `retorno-${fileId}.RET`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) toast.error('Bytes brutos não disponíveis (arquivo enviado antes da V4.86).');
      else toast.error('Falha ao baixar o .RET.');
    }
  };
  const estornar = async (titleId, fileId) => {
    const reason = window.prompt('Justificativa do estorno (mínimo 8 caracteres):');
    if (!reason || reason.trim().length < 8) { if (reason !== null) toast.error('Justificativa muito curta.'); return; }
    try {
      const { data } = await api.post('/dnapay/safra/retorno/estornar', { title_id: titleId, file_id: fileId, reason: reason.trim() });
      toast.success(`Baixa estornada · título voltou para "${data?.new_status || 'pendente'}".`);
      if (auditOpen === fileId) await openAudit(fileId); // refresh
      await fetchDivergences();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao estornar.');
    }
  };

  // V4.84 · Card de divergências (títulos pagos no CNAB mas ainda não refletidos)
  const [divergences, setDivergences] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const fetchDivergences = async () => {
    try {
      const r = await api.get('/dnapay/safra/retorno/divergences-count');
      setDivergences(r.data);
    } catch (_e) { /* ignore */ }
  };
  const runReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    try {
      const r = await api.post('/dnapay/safra/retorno/reconcile-divergences?dry_run=false');
      const d = r.data;
      toast.success(`${d?.corrected || 0} título(s) reconciliado(s) · ${d?.already_ok || 0} já ok · ${d?.unresolved || 0} sem par`);
      await fetchDivergences();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao reconciliar');
    } finally { setReconciling(false); }
  };

  const [uploadProgress, setUploadProgress] = useState(null); // {status, progress_percent, processed_records, total_records, matched_records, ...}

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setUploadProgress({ status: 'uploading', progress_percent: 0, stage_msg: 'Enviando arquivo...' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      // V4.44 · upload assíncrono (sem HTTP 524)
      const upResp = await fetch(`${API}/api/dnapay/safra/retorno/upload`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      if (!upResp.ok) {
        const j = await upResp.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${upResp.status}`);
      }
      const up = await upResp.json();
      const batch_id = up.batch_id;
      toast.success('Arquivo recebido. Processando em segundo plano.');
      setUploadProgress({ status: 'queued', progress_percent: 1, batch_id });
      // Polling do status
      let previewId = null;
      for (let attempt = 0; attempt < 300; attempt++) {
        const st = await api.get(`/dnapay/safra/retorno/batches/${batch_id}/status`).then(r => r.data);
        setUploadProgress({ ...st, batch_id });
        if (st.status === 'preview_ready') { previewId = st.preview_id; break; }
        if (st.status === 'error') throw new Error(st.stage_msg || 'Erro no processamento');
        await new Promise(res => setTimeout(res, 1500));
      }
      if (!previewId) throw new Error('Timeout aguardando processamento');
      const { data } = await api.get(`/dnapay/safra/retorno/previews/${previewId}`);
      setPreview(data);
      setUploadProgress(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Falha ao processar arquivo de retorno').slice(0, 300));
      setUploadProgress(null);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const [applyProgress, setApplyProgress] = useState(null); // {status, progress_percent, ...}

  const onConfirm = async (approveSigs) => {
    if (!preview) return;
    // V4.49 · fix P0 "Field required": endpoint espera `preview_id`, mas o objeto vindo de
    // GET /previews/{id} tem chave `id`. Aceitamos ambos para robustez.
    const previewId = preview.preview_id || preview.id;
    if (!previewId) {
      toast.error('Prévia sem identificador. Refaça o upload.');
      return;
    }
    console.log('CNAB_CONFIRM_CLICK', {
      preview_id: previewId,
      selected: approveSigs.length,
      total_records: (preview.records || []).length,
    });
    setConfirming(true);
    setApplyProgress({ status: 'queued', progress_percent: 0, total_selected: approveSigs.length, stage_msg: 'Enviando confirmação...' });
    try {
      // V4.47 · endpoint assíncrono (evita CF 524 e tela branca)
      const { data } = await api.post('/dnapay/safra/retorno/confirmar-async', {
        preview_id: previewId,
        approve_signatures: approveSigs,
      });
      console.log('CNAB_CONFIRM_ACK', data);
      if (data?.already_processed) {
        toast.info('Arquivo já havia sido processado. Nenhuma baixa duplicada foi aplicada.');
        setApplyProgress(null);
        setPreview(null); fetchData();
        return;
      }
      const jobId = data.apply_job_id;
      // polling
      for (let attempt = 0; attempt < 600; attempt++) {
        const st = await api.get(`/dnapay/safra/retorno/apply-status/${jobId}`).then(r => r.data);
        setApplyProgress(st);
        if (st.status === 'done' || st.status === 'done_with_errors' || st.status === 'error') {
          if (st.status === 'error') toast.error(`Falha ao aplicar: ${st.error_detail || 'erro desconhecido'}`);
          else if (st.status === 'done_with_errors') toast.warning(`Aplicado com avisos: ${st.paid} pagos, ${st.failed} falhas`);
          else toast.success(`Baixas aplicadas: ${st.paid} pagos · ${st.already_paid || 0} já pagos · ${st.skipped || 0} pulados`);
          break;
        }
        await new Promise(res => setTimeout(res, 1500));
      }
      setApplyProgress(null);
      setPreview(null);
      fetchData();
    } catch (err) {
      const msg = getErrorMessage(err, 'Falha ao confirmar baixa');
      console.error('CNAB_CONFIRM_ERROR', {
        status: err?.response?.status,
        detail: err?.response?.data?.detail,
        response: err?.response?.data,
        parsed: msg,
      });
      toast.error(msg.slice(0, 300));
      setApplyProgress(null);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4 p-1" data-testid="dnapay-retornos">
      {/* V4.47 · Overlay de progresso da aplicação (bloqueia tela branca) */}
      {applyProgress && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="apply-progress-overlay">
          <div className="bg-white rounded-2xl border border-purple-200 p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
              <div className="flex-1">
                <div className="text-base font-bold text-slate-900">
                  Aplicando {applyProgress.total_selected || 0} baixas...
                </div>
                <div className="text-xs text-slate-500">
                  {applyProgress.status === 'queued' && 'Aguardando início...'}
                  {applyProgress.status === 'processing' && 'Processando em segundo plano — não feche a janela.'}
                  {applyProgress.status === 'done' && '✓ Concluído com sucesso'}
                  {applyProgress.status === 'done_with_errors' && '⚠ Concluído com avisos'}
                  {applyProgress.status === 'error' && '✗ Falhou — veja detalhes abaixo'}
                </div>
              </div>
              <div className="text-2xl font-bold text-purple-700">{applyProgress.progress_percent || 0}%</div>
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-purple-600 transition-all duration-300" style={{ width: `${applyProgress.progress_percent || 0}%` }} />
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="bg-slate-50 rounded p-2"><div className="text-slate-500">Processados</div><div className="text-lg font-bold text-slate-900">{applyProgress.processed || 0}</div></div>
              <div className="bg-emerald-50 rounded p-2"><div className="text-slate-500">Pagos</div><div className="text-lg font-bold text-emerald-700">{applyProgress.paid || 0}</div></div>
              <div className="bg-amber-50 rounded p-2"><div className="text-slate-500">Já pagos</div><div className="text-lg font-bold text-amber-700">{applyProgress.already_paid || 0}</div></div>
              <div className="bg-red-50 rounded p-2"><div className="text-slate-500">Falhas</div><div className="text-lg font-bold text-red-700">{applyProgress.failed || 0}</div></div>
            </div>
            {applyProgress.error_detail && (
              <div className="mt-4 text-xs bg-red-50 border border-red-200 rounded p-2 text-red-700">{applyProgress.error_detail}</div>
            )}
            {(applyProgress.status === 'done' || applyProgress.status === 'done_with_errors' || applyProgress.status === 'error') && (
              <button type="button" onClick={() => setApplyProgress(null)} className="mt-4 w-full px-3 py-2 rounded bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700">Fechar</button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-purple-900">Retornos CNAB · Banco Safra 422</h1>
          <p className="text-xs text-slate-500">
            Leitura posicional CNAB 400 (Cobrança). Toda baixa passa por <b>prévia</b> antes de aplicar —
            valores em centavos, principal e acréscimos separados, idempotência SHA-256.
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            id="dnapay-retorno-input"
            type="file"
            accept=".ret,.crt,.txt,.RET,.CRT,.TXT"
            className="hidden"
            onChange={onUpload}
            data-testid="ret-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={busy || confirming}
            className="bg-gradient-to-r from-purple-700 to-emerald-600 text-white"
            data-testid="ret-upload-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
            Enviar arquivo de retorno
          </Button>
        </div>
      </div>

      {uploadProgress && (
        <div className="bg-white rounded-2xl border border-purple-200 p-4 my-4" data-testid="cnab-progress-panel">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">
                {uploadProgress.status === 'queued' && 'Arquivo recebido. Aguardando processamento...'}
                {uploadProgress.status === 'parsing' && 'Interpretando arquivo CNAB...'}
                {uploadProgress.status === 'matching' && 'Conciliando com títulos...'}
                {uploadProgress.status === 'uploading' && 'Enviando arquivo...'}
              </div>
              <div className="text-xs text-slate-500">{uploadProgress.stage_msg || ''}</div>
            </div>
            <div className="text-lg font-bold text-purple-600">{uploadProgress.progress_percent || 0}%</div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-purple-600 transition-all" style={{ width: `${uploadProgress.progress_percent || 0}%` }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <div className="bg-slate-50 rounded px-2 py-1.5"><span className="text-slate-500">Registros:</span> <b>{uploadProgress.processed_records || 0}/{uploadProgress.total_records || '?'}</b></div>
            <div className="bg-emerald-50 rounded px-2 py-1.5"><span className="text-slate-500">Match:</span> <b className="text-emerald-700">{uploadProgress.matched_records || 0}</b></div>
            <div className="bg-amber-50 rounded px-2 py-1.5"><span className="text-slate-500">Ambíguos:</span> <b className="text-amber-700">{uploadProgress.ambiguous_records || 0}</b></div>
            <div className="bg-slate-100 rounded px-2 py-1.5"><span className="text-slate-500">Não achados:</span> <b>{uploadProgress.not_found_records || 0}</b></div>
            <div className="bg-red-50 rounded px-2 py-1.5"><span className="text-slate-500">Erros:</span> <b className="text-red-700">{uploadProgress.errors || 0}</b></div>
          </div>
        </div>
      )}

      {/* V4.84 · Card compacto de divergências */}
      {divergences && divergences.pending_count > 0 && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="dnapay-divergences-card">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold">!</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">{divergences.pending_count} título(s) aguardando reconciliação</div>
              <div className="text-[11px] text-amber-800">Baixados no CNAB mas ainda não refletidos em db.titles. Rode a reconciliação para consertar em massa.</div>
            </div>
            <button
              type="button"
              onClick={runReconcile}
              disabled={reconciling}
              className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
              data-testid="dnapay-reconcile-run"
            >{reconciling ? 'Reconciliando…' : 'Reconciliar agora'}</button>
          </CardContent>
        </Card>
      )}

      {resumo && (
        <div className="space-y-3 my-4" data-testid="cnab-monthly-cards">
          {(() => {
            const mes = (resumo.months || []).find((m) => m.month === mesSel) || resumo.current || {};
            return (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Retornos recebidos · {mes.label || '—'}
                  </p>
                  <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-600">
                    Mês dos cards
                    <select className="h-8 rounded-md border px-2 text-[12px] bg-white"
                      value={mesSel || (resumo.current?.month || '')}
                      onChange={(e) => setMesSel(e.target.value)}
                      data-testid="cnab-month-filter">
                      {(resumo.months || []).map((m) => (
                        <option key={m.month} value={m.month}>{m.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  <MesCard label={`Recebido em ${mes.label || '—'}`}
                    value={fmtBRL(mes.total)} tone="purple" testid="cnab-card-total" />
                  <MesCard label="Principal" value={fmtBRL(mes.principal)}
                    tone="emerald" testid="cnab-card-principal" />
                  <MesCard label={mes.charges_split ? 'Multa recebida' : 'Acréscimos recebidos'}
                    value={fmtBRL(mes.late_fee ?? mes.additional)}
                    hint={mes.charges_split ? null : 'retorno sem separação multa/juros'}
                    tone="amber" testid="cnab-card-multa" />
                  <MesCard label="Juros recebidos"
                    value={mes.interest != null ? fmtBRL(mes.interest) : '—'}
                    hint={mes.charges_split ? null : 'sem separação no retorno'}
                    tone="amber" testid="cnab-card-juros" />
                  <MesCard label="Arquivos" value={`${mes.files ?? 0}`}
                    tone="slate" testid="cnab-card-arquivos" />
                  <MesCard label="Baixas aplicadas" value={`${mes.settlements ?? 0}`}
                    tone="blue" testid="cnab-card-baixas" />
                </div>
              </>
            );
          })()}

          {(resumo.months || []).length > 0 && (
            <div className="rounded-2xl border bg-white overflow-x-auto" data-testid="cnab-monthly-summary">
              <p className="px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-600 border-b">
                Resumo por mês
              </p>
              <table className="w-full text-sm min-w-[620px]">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Mês</th>
                    <th className="text-right px-3 py-2">Principal</th>
                    <th className="text-right px-3 py-2">Multa / acréscimos</th>
                    <th className="text-right px-3 py-2">Juros</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-center px-3 py-2">Arquivos</th>
                    <th className="text-center px-3 py-2">Baixas</th>
                  </tr>
                </thead>
                <tbody>
                  {resumo.months.map((m) => (
                    <tr key={m.month}
                      onClick={() => setMesSel(m.month)}
                      className={`border-t cursor-pointer transition-colors hover:bg-purple-50/60 ${
                        (mesSel || resumo.current?.month) === m.month ? 'bg-purple-50' : ''}`}
                      data-testid={`cnab-month-${m.month}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800 capitalize">{m.label}</td>
                      <td className="px-3 py-2 text-right text-emerald-800">{fmtBRL(m.principal)}</td>
                      <td className="px-3 py-2 text-right text-amber-800">
                        {m.late_fee != null ? fmtBRL(m.late_fee)
                          : <span title="Retorno sem separação de multa/juros">acr. {fmtBRL(m.additional)}</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-amber-800">
                        {m.interest != null ? fmtBRL(m.interest) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-purple-900">{fmtBRL(m.total)}</td>
                      <td className="px-3 py-2 text-center">{m.files ?? 0}</td>
                      <td className="px-3 py-2 text-center">{m.settlements ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center gap-3">
          <FileText className="w-5 h-5 text-purple-700" />
          <CardTitle className="text-base">Histórico de arquivos processados ({arquivos.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DnaTableV4 stickyHeader testId="dnapay-retornos-table-v4">
            <table className="w-full text-sm">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Arquivo</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-center px-3 py-2">Data arquivo</th>
                  <th className="text-center px-3 py-2">Registros</th>
                  <th className="text-center px-3 py-2">Baixados</th>
                  <th className="text-center px-3 py-2">Ambíguos</th>
                  <th className="text-center px-3 py-2">Não localizados</th>
                  <th className="text-right px-3 py-2">Principal recebido</th>
                  <th className="text-right px-3 py-2">Multa</th>
                  <th className="text-right px-3 py-2">Juros</th>
                  <th className="text-right px-3 py-2">Total recebido</th>
                  <th className="text-center px-3 py-2">Processado em</th>
                  <th className="text-center px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {arquivos.length === 0 && (
                  <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-400">
                    Nenhum arquivo processado ainda.<br />
                    <span className="text-xs">Envie o arquivo <b>.RET</b> recebido do Banco Safra para
                      abrir a prévia antes da baixa.</span>
                  </td></tr>
                )}
                {arquivos.map((f) => (
                  <React.Fragment key={f.id}>
                  <tr data-testid={`retorno-file-${f.id.slice(0, 8)}`}>
                    <td className="px-3 py-2 text-slate-800">
                      <button type="button" onClick={() => openAudit(f.id)} className="text-left w-full hover:underline" data-testid={`retorno-file-audit-${f.id.slice(0, 8)}`}>
                        <div className="font-semibold text-purple-800">{f.filename}</div>
                        <div className="text-[10px] font-mono text-slate-400">{(f.file_hash || '').slice(0, 12)}…</div>
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {f.reverted ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${f.reprocessed_at ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-amber-100 text-amber-800 border border-amber-200'}`} title={f.revert_reason || ''} data-testid={`retorno-status-${f.id.slice(0, 8)}`}>
                          <Undo2 className="w-3 h-3" />
                          {f.status_display || (f.reprocessed_at ? 'Reprocessado' : 'Revertido')}
                        </span>
                      ) : f.confirmed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`retorno-status-${f.id.slice(0, 8)}`}>
                          <CheckCircle2 className="w-3 h-3" /> Processado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200" data-testid={`retorno-status-${f.id.slice(0, 8)}`}>Pendente</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-700">{fmtDate(f.file_date)}</td>
                    <td className="px-3 py-2 text-center">{f.n_records || 0}</td>
                    <td className="px-3 py-2 text-center">
                      {((f.baixados_real ?? f.n_settled) || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" title={f.baixados_real != null && f.baixados_real !== f.n_settled ? `Recalculado do estado real em db.titles (arquivo declara ${f.n_settled || 0})` : undefined}>
                          <CheckCircle2 className="w-3.5 h-3.5" />{f.baixados_real ?? f.n_settled}
                        </span>
                      ) : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(f.n_ambiguous || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" />{f.n_ambiguous}</span>
                      ) : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(f.n_not_found || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="w-3.5 h-3.5" />{f.n_not_found}</span>
                      ) : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-800 font-semibold">
                      {fmtBRL(f.valores?.principal ?? f.totals?.paid_amount)}
                    </td>
                    <td className="px-3 py-2 text-right text-amber-800">
                      {f.valores?.late_fee != null ? fmtBRL(f.valores.late_fee)
                        : <span className="text-slate-400" title="O retorno do banco não separou multa/juros">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-amber-800">
                      {f.valores?.interest != null ? fmtBRL(f.valores.interest)
                        : <span className="text-slate-400" title={`Acréscimos (sem separação): ${fmtBRL(f.valores?.additional ?? f.totals?.additions_amount)}`}>
                            acr. {fmtBRL(f.valores?.additional ?? f.totals?.additions_amount)}
                          </span>}
                    </td>
                    <td className="px-3 py-2 text-right text-purple-900 font-bold">
                      {fmtBRL(f.valores?.total ?? f.totals?.paid_amount)}
                    </td>
                    <td className="px-3 py-2 text-center text-[12px] text-slate-600">
                      {f.confirmed_at ? new Date(f.confirmed_at).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {!f.reverted && f.confirmed && (
                        <button
                          type="button"
                          onClick={() => { setRevertTarget(f); setRevertReason(''); setRevertMode('processing_only'); }}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 font-semibold"
                          title="Reverter para permitir nova leitura"
                          data-testid={`retorno-revert-${f.id.slice(0, 8)}`}
                        >
                          <Undo2 className="w-3 h-3" /> Reverter
                        </button>
                      )}
                      {f.reverted && (
                        <span className="text-[10px] text-slate-500" title={`${f.revert_mode} · ${f.revert_reason || ''}`}>
                          por {f.reverted_by_name || '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                  {auditOpen === f.id && auditData && (
                    <tr>
                      <td colSpan={13} className="p-3 bg-slate-50 border-t border-slate-200" data-testid={`retorno-audit-${f.id.slice(0, 8)}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="text-sm font-semibold text-slate-800">Auditoria · {auditData.filename}</div>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">SETTLED {auditData.totals.settled}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">CONCILIATED {auditData.totals.conciliated}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">STUCK {auditData.totals.stuck}</span>
                          <button type="button" onClick={() => downloadRet(f.id, auditData.filename)} className="ml-auto text-[11px] px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-800" data-testid={`retorno-download-${f.id.slice(0, 8)}`}>Baixar .RET</button>
                          <button type="button" onClick={() => { setAuditOpen(null); setAuditData(null); }} className="text-[11px] px-2 py-1 rounded bg-white border border-slate-200 text-slate-600">Fechar</button>
                        </div>
                        {['settled', 'stuck', 'conciliated'].map((cat) => (
                          (auditData[cat] || []).length > 0 && (
                            <div key={cat} className="mt-2">
                              <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">{cat}</div>
                              <div className="border border-slate-200 rounded overflow-hidden">
                                <table className="w-full text-[11px]">
                                  <thead className="bg-slate-100 text-slate-600"><tr>
                                    <th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Nosso número</th><th className="px-2 py-1 text-left">Aluno</th><th className="px-2 py-1 text-right">Valor</th><th className="px-2 py-1 text-left">Status título</th><th className="px-2 py-1"></th></tr></thead>
                                  <tbody>
                                    {auditData[cat].map((row) => (
                                      <tr key={`${cat}-${row.sequence_number}`} className="border-t border-slate-100">
                                        <td className="px-2 py-1 text-slate-500">{row.sequence_number}</td>
                                        <td className="px-2 py-1 font-mono text-slate-800">{row.our_number || '—'}</td>
                                        <td className="px-2 py-1 text-slate-700">{row.student_name || '—'}</td>
                                        <td className="px-2 py-1 text-right text-slate-800">{fmtBRL(row.paid_amount)}</td>
                                        <td className="px-2 py-1 text-slate-600">{row.title_status || row.reason || '—'}</td>
                                        <td className="px-2 py-1 text-right">
                                          {cat === 'settled' && row.title_id && (
                                            <button type="button" onClick={() => estornar(row.title_id, f.id)} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 font-semibold" data-testid={`retorno-estornar-${row.title_id.slice(0, 8)}`}>Estornar</button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )
                        ))}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </DnaTableV4>
        </CardContent>
      </Card>

      <PreviewModal
        preview={preview}
        onClose={() => setPreview(null)}
        onConfirm={onConfirm}
        confirming={confirming}
      />

      {/* V4.92 · Modal Reverter Retorno */}
      <Dialog open={!!revertTarget} onOpenChange={(v) => { if (!v && !reverting) { setRevertTarget(null); setRevertReason(''); } }}>
        <DialogContent className="max-w-lg" data-testid="dnapay-revert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-4 h-4 text-amber-700" /> Reverter retorno CNAB
            </DialogTitle>
          </DialogHeader>
          {revertTarget && (
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-[11px] uppercase font-bold text-slate-500">Arquivo</div>
                <div className="font-semibold text-slate-800">{revertTarget.filename}</div>
                <div className="text-[11px] text-slate-500">
                  {revertTarget.n_records || 0} registro(s) · baixados: <b>{revertTarget.baixados_real ?? revertTarget.n_settled ?? 0}</b>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-700">Modo de reversão</div>
                <label className="flex items-start gap-2 p-3 rounded border cursor-pointer hover:bg-slate-50 border-slate-200" data-testid="dnapay-revert-mode-a">
                  <input
                    type="radio"
                    name="revert-mode"
                    className="mt-1"
                    checked={revertMode === 'processing_only'}
                    onChange={() => setRevertMode('processing_only')}
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-emerald-800 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Reverter somente processamento (recomendado)</div>
                    <div className="text-[11px] text-slate-600">Libera o arquivo para nova leitura/re-upload. Mantém pagamentos reais em db.titles. Na nova prévia, títulos já pagos aparecem como <b>Já processado</b>.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 rounded border cursor-pointer hover:bg-rose-50 border-rose-200" data-testid="dnapay-revert-mode-b">
                  <input
                    type="radio"
                    name="revert-mode"
                    className="mt-1"
                    checked={revertMode === 'processing_and_payments'}
                    onChange={() => setRevertMode('processing_and_payments')}
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-rose-800 flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> Reverter processamento + baixas aplicadas</div>
                    <div className="text-[11px] text-rose-700">
                      Desfaz baixas em db.titles apenas para pagamentos com <b>payment_origin</b> CNAB deste arquivo.
                      Não toca pagamentos manuais/PIX/cartão/outro CNAB. Recomputa status por vencimento.
                    </div>
                  </div>
                </label>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Motivo <span className="text-rose-600">*</span>
                </label>
                <textarea
                  className="w-full text-sm border border-slate-300 rounded p-2 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="Ex.: baixas parciais causadas por bug do limite de 50; refazer leitura completa"
                  value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)}
                  data-testid="dnapay-revert-reason"
                />
                <div className="text-[11px] text-slate-500 mt-1">Mínimo 8 caracteres. Será gravado no histórico de auditoria.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRevertTarget(null); setRevertReason(''); }} disabled={reverting} data-testid="dnapay-revert-cancel">Cancelar</Button>
            <Button
              onClick={doRevert}
              disabled={reverting || (revertReason.trim().length < 8)}
              className={revertMode === 'processing_and_payments'
                ? 'bg-rose-600 hover:bg-rose-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'}
              data-testid="dnapay-revert-confirm"
            >
              {reverting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Undo2 className="w-4 h-4 mr-2" />}
              {revertMode === 'processing_and_payments' ? 'Reverter + Desfazer baixas' : 'Reverter processamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MES_TONES = {
  purple: 'border-purple-200 bg-purple-50/60 text-purple-900',
  emerald: 'border-emerald-200 bg-emerald-50/60 text-emerald-900',
  amber: 'border-amber-200 bg-amber-50/60 text-amber-900',
  blue: 'border-blue-200 bg-blue-50/60 text-blue-900',
  slate: 'border-slate-200 bg-slate-50 text-slate-800',
};

const MesCard = ({ label, value, hint, tone = 'slate', testid }) => (
  <div className={`rounded-2xl border p-3 shadow-sm transition-all hover:shadow-md ${MES_TONES[tone]}`}
    data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-wider opacity-75 truncate">{label}</p>
    <p className="mt-1.5 text-lg font-extrabold leading-none">{value}</p>
    {hint && <p className="mt-1 text-[10px] opacity-70 truncate">{hint}</p>}
  </div>
);
